import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FileText, Image } from "lucide-react";
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { areaMeasurements, unitTypes } from '../../../utils/data';
import { getItem, setItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import {
    getUnitDocumentsURL,
    getUnitInvoiceTransactionsURL,
    getUnitMeterHistory,
    getUnitOccupantsURL,
    getUnitUrl,
    unitStatementOfAccountsURL,
    updateUnit,
    uploadUnitDocumentURL,
    getDocumentTypesURL,
    getBillerAddressesURL
} from '../../../utils/urls';
import Layout from '../component/layout';
import HandoverInspectionTab from './HandoverInspectionTab';


const ViewUnit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { unitId } = useParams();
    const [unit, setUnit] = useState({})
    const [meterData, setMeterData] = useState(null);
    const [powerMeterData, setPowerMeterData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [unitName, setUnitName] = useState('');
    const [unitType, setUnitType] = useState('')
    const [floorUnit, setFloorUnit] = useState('');
    const [lrNumber, setLrNumber] = useState('');
    const [netLettableArea, setNetLettableArea] = useState('');
    const [tenant, setTenant] = useState({});
    const [homeOwner, setHomeOwner] = useState({});
    const [resident, setResident] = useState({});
    const defaultMeasurement = areaMeasurements.find(measurement => measurement.default) || { symbol: '' };
    const [activeSubTab, setActiveSubTab] = useState('list');
    const [documentModal, setDocumentModal] = useState(false);
    const [documentName, setDocumentName] = useState('');
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentType, setDocumentType] = useState('');
    const [file, setFile] = useState(null);
    const [unitDocuments, setUnitDocuments] = useState([]);
    const [occupants, setOccupants] = useState([]);
    const [activeOccupants, setActiveOccupants] = useState([]);
    const [pastOccupants, setPastOccupants] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [agingData, setAgingData] = useState([]);
    const [summary, setSummary] = useState([]);
    const [invoiceTransactions, setInvoiceTransactions] = useState([]);
    const [allTransactions, setAllTransactions] = useState([]);
    const [activeTab, setActiveTab] = useState('profile-1');
    const [infoVisible, setInfoVisible] = useState(activeTab === 'profile-1');
    const [isManagedByPropertyManager, setIsManagedByPropertyManager] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [billerAddressList, setBillerAddressList] = useState([]);
    const [facilityLogo, setFacilityLogo] = useState(
        () => localStorage.getItem("selectedFacilityLogo") || "",
    );

    // Filter options for dropdown
    const filterOptions = [
        { label: 'All Transactions', value: 'all' },
        { label: 'Levy Only', value: 'levy' },
        { label: 'Lease Only', value: 'lease' },
        { label: 'Water Only', value: 'water' }
    ];

    // Add this toggle function
    const toggleInfoPanel = () => {
        setInfoVisible(!infoVisible);
    };

    // Update tab click handler 
    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        // Show info panel only when on profile-1 tab
        setInfoVisible(tabId === 'profile-1');
    };

    console.log("facilityLogo:", facilityLogo);
    console.log("facilityLogo type:", typeof facilityLogo);

    const getFacilityLogo = async () => {
        try {
            const facilityFromState = location.state?.facility;

            if (facilityFromState?.logo) {
                await setItem("selectedFacilityLogo", facilityFromState.logo);
                setFacilityLogo(facilityFromState.logo);
                return;
            }

            const storedLogo = await getItem("selectedFacilityLogo");
            setFacilityLogo(storedLogo || "");
        } catch (error) {
            console.error("Error fetching facility logo:", error);

            const fallbackLogo = localStorage.getItem("selectedFacilityLogo");
            setFacilityLogo(fallbackLogo || "");
        }
    };

    const fetchWaterMeterHistory = async () => {
        try {
            setLoading(true);
            const facilityId = await getItem('selectedFacilityId');

            if (!facilityId || !unitId) {
                throw new Error('Facility ID or Unit ID not available');
            }

            const response = await makeRequest2(`${getUnitMeterHistory}/${facilityId}/${unitId}`, 'GET');
            // Check if response has the expected structure with data.meter property
            if (response && response.success === true && response.data && response.data.meter) {
                setMeterData(response.data.meter);
            } else {
                setMeterData(null);
            }
        } catch (err) {
            toastify(err.message, 'error');
            setMeterData(null);
        } finally {
            setLoading(false);
        }
    };

    const navigateToWaterMeter = (meterId, meterType) => {
        if (meterType === 'smart') {
            navigate(`/facility/utility_management/meter_management/view_water_meter/${meterId}`);
        } else if (meterType === 'analog') {
            navigate('/facility/utility_management/meter_management/water_meters/ViewAnalogMeter', {
                state: { meterId: meterId }
            });
        }
    };

    // Navigation handler for power meters
    const navigateToPowerMeter = (meterId) => {
        navigate(`/facility/utility_management/meter_management/view_power_meter/${meterId}`);
    };

    // Template for clickable water meter number
    const waterMeterNumberTemplate = (rowData) => (
        <span
            onClick={() => navigateToWaterMeter(rowData._id, rowData.meterType)}
            style={{
                color: '#007bff',
                cursor: 'pointer'
            }}
            className="meter-link"
        >
            {rowData.meterNumber}
        </span>
    );

    // Template for clickable power meter serial number
    const powerMeterSerialTemplate = (rowData) => (
        <span
            onClick={() => navigateToPowerMeter(rowData._id)}
            style={{
                color: '#007bff',
                cursor: 'pointer'
            }}
            className="meter-link"
        >
            {rowData.meterSerialNumber}
        </span>
    );

    const fetchPowerMeterHistory = async () => {
        try {
            setLoading(true);
            const facilityId = await getItem('selectedFacilityId');

            if (!facilityId || !unitId) {
                throw new Error('Facility ID or Unit ID not available');
            }
            // Use the new endpoint to get unit meter details
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/power_meters/get_unit_meter/${unitId}`,
                'GET'
            );
            // Check if response has the expected structure
            if (response && response.success === true && response.data.data) {
                setPowerMeterData(response.data.data);
            } else {
                setPowerMeterData(null);
            }
        } catch (err) {
            console.error('Error fetching power meter:', err);
            toastify('No power meter found for this unit', 'info');
            setPowerMeterData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatReading = (value) => {
        return value ? `${Number(value).toFixed(2)} m³` : '0.00 m³';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const statusTemplate = (rowData) => {
        const statusStyles = {
            maintenance: { color: '#ffc107', backgroundColor: '#fff8e1' },
            opened: { color: '#28a745', backgroundColor: '#e9fbe9' },
            faulty: { color: '#dc3545', backgroundColor: '#fde9e9' },
            closed: { color: '#6c757d', backgroundColor: '#f1f1f1' }
        };
        const style = statusStyles[rowData.status] || { color: '#6c757d', backgroundColor: '#f1f1f1' };
        return (
            <span
                style={{
                    ...style,
                    padding: '0.5em 1em',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'inline-block',
                }}
            >
                {rowData.status || 'unknown'}
            </span>
        );
    };

    const powerStatusTemplate = (rowData) => {
        const statusStyles = {
            maintenance: { color: '#ffc107', backgroundColor: '#fff8e1' },
            on: { color: '#28a745', backgroundColor: '#e9fbe9' },
            faulty: { color: '#dc3545', backgroundColor: '#fde9e9' },
            off: { color: '#6c757d', backgroundColor: '#f1f1f1' }
        };
        const style = statusStyles[rowData.liveStatus] || { color: '#6c757d', backgroundColor: '#f1f1f1' };
        return (
            <span
                style={{
                    ...style,
                    padding: '0.5em 1em',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'inline-block',
                }}
            >
                {rowData.status || 'unknown'}
            </span>
        );
    };

    const getUnit = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(getUnitUrl + '/' + facilityId + '/' + unitId, 'GET')

            if (response.success === true) {
                const unit = response.data;
                setUnit(unit)
                setUnitName(unit.name);
                setUnitType(unit.unitType);
                setFloorUnit(unit.floorUnitNo);
                setLrNumber(unit.landRateNumber);
                setNetLettableArea(unit.netLettableArea);
                setIsManagedByPropertyManager(unit.isManagedByPropertyManager || false);
                setHomeOwner(unit.homeOwner || null);
                setTenant(unit.tenant || null);
                setResident(unit.resident || null);
                // Set tenant/resident ID in local storage
                const tenantId = unit.tenantId || unit.residentId || null;
                if (tenantId) {
                    await setItem('customerId', tenantId);
                }
            }
            else {
                throw new Error(response.error)
            }
        }
        catch (err) {
            toastify(err.message, 'error')
        }
    }

    const getUnitOccupants = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(getUnitOccupantsURL + '/' + facilityId + '/' + unitId, 'GET');

            console.log("response", response);

            if (response.success === true) {
                const data = response.data;
                setUnitName(data.unitName);
                setOccupants(data.occupants);

                const activeOccupants = data.occupants.filter(
                    occ =>
                        occ.isActive &&
                        (
                            occ.customerType === "tenant" ||
                            (occ.customerType === "home owner" && occ.residentType === "resident")
                        )
                );

                const pastOccupants = data.occupants.filter(
                    occ => !occ.isActive && occ.customerType === "tenant"
                );
                setActiveOccupants(activeOccupants);
                setPastOccupants(pastOccupants);
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, 'error');
        }
    };

    const HandleUnitUpdate = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const updatedUnit = {
                unitName,
                unitType,
                floorUnit,
                lrNumber
            };
            const response = await makeRequest2(`${updateUnit}/${facilityId}/${unitId}`, 'POST', updatedUnit);

            if (response.success) {
                toastify(response.message || 'Unit updated successfully', 'success');
                getUnit(); // Refresh the unit data
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, 'error');
        }
    };

    const fetchDocumentTypes = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getDocumentTypesURL}/${facilityId}`, "GET");

            if (response.success) {
                const dataArray = Array.isArray(response?.data?.data)
                    ? response.data.data
                    : [];

                setDocumentTypes(dataArray);
            } else {
                setDocumentTypes([]);
                toastify("Failed to load document types", "error");
            }
        } catch (error) {
            setDocumentTypes([]);
            toastify(error.message, "error");
        }
    };

    const handleDocumentSubmit = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const formData = new FormData();
            formData.append('documentName', documentName);
            formData.append('documentType', documentType);
            if (file) {
                formData.append('file', file);
            }

            const response = await makeRequest2(`${uploadUnitDocumentURL}/${facilityId}/${unitId}`, 'POST', formData);

            if (response.success) {
                toastify('Document added successfully', 'success');
                setDocumentName('');
                setDocumentType('');
                setFile(null);
                setDocumentModal(false);
                getUnitDocuments();
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, 'error')
        }
    }

    const getUnitDocuments = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getUnitDocumentsURL}/${facilityId}/${unitId}`, 'GET', {});

            if (response.success) {
                setUnitDocuments(response.data.documents || []);
            } else {
                setUnitDocuments([]);
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, 'error')
        }
    }

    const renderDocument = (rowData) => {
        if (!rowData.document) return "No document";
        const fileUrl = `${rowData.document}`;
        const isImage = rowData.document.match(/\.(jpeg|jpg|png|gif)$/i); // Check if it's an image
        return (
            <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center text-primary"
                style={{ textDecoration: "none", gap: "5px" }}
            >
                {isImage ? <Image size={18} /> : <FileText size={18} />}
                View Document
            </a>
        );
    };

    // Combined fetch function for all transaction types
    const fetchStatementOfAccounts = async (filterType = 'all') => {
        setLoading(true);
        try {
            const facilityId = await getItem('selectedFacilityId');
            const customerId = await getItem('customerId');

            let combinedTransactions = [];
            let combinedAgingData = {
                aging15: 0,
                aging30: 0,
                aging60: 0,
                aging90: 0,
                totalOutstanding: 0
            };

            // Fetch levy/lease data based on filter
            if (filterType === 'all' || filterType === 'levy' || filterType === 'lease') {
                const queryParam = (filterType === 'levy' || filterType === 'lease')
                    ? `?invoiceType=${filterType}`
                    : '';
                const url = `${unitStatementOfAccountsURL}/${facilityId}/${customerId}/${unitId}${queryParam}`;
                const response = await makeRequest2(url, "GET", {});

                if (response.success) {
                    const transactions = response.data.transactions || [];

                    setSummary(response.data.summary || {});

                    let filteredTransactions = transactions;

                    if (filterType === 'levy') {
                        filteredTransactions = transactions.filter(t =>
                            t.invoiceType?.toLowerCase() === 'contract' ||
                            t.invoiceType?.toLowerCase() === 'levy'
                        );
                    } else if (filterType === 'lease') {
                        filteredTransactions = transactions.filter(t =>
                            t.invoiceType?.toLowerCase() === 'lease'
                        );
                    }

                    const typedTransactions = filteredTransactions.map(t => {
                        let invoiceType = t.invoiceType;

                        if (!invoiceType) {
                            if (filterType === 'levy') {
                                invoiceType = 'contract';
                            } else if (filterType === 'lease') {
                                invoiceType = 'lease';
                            } else {
                                invoiceType = 'unknown';
                            }
                        }

                        return {
                            ...t,
                            invoiceType
                        };
                    });

                    combinedTransactions = [...typedTransactions];

                    // Calculate aging from the filtered transactions
                    combinedAgingData = response.data.agingSummary || {
                        aging15: 0,
                        aging30: 0,
                        aging60: 0,
                        aging90: 0,
                        totalOutstanding: 0
                    };
                }
            }

            // Fetch water data if needed
            if (filterType === 'all' || filterType === 'water') {
                try {
                    // First get meter number
                    const meterResponse = await makeRequest2(`${getUnitMeterHistory}/${facilityId}/${unitId}`, 'GET');

                    if (meterResponse?.success && meterResponse.data?.meter?.meterNumber) {
                        const meterNumber = meterResponse.data.meter.meterNumber;

                        // Then get SOA using the meterNumber
                        const waterUrl = `/api/app/water-invoices/get_customer_soa/${meterNumber}/${customerId}`;
                        const waterResponse = await makeRequest2(waterUrl, 'GET');

                        if (waterResponse.success && waterResponse.data?.data) {
                            const waterData = waterResponse.data.data;

                            // Transform water invoices to match levy/lease structure
                            const waterTransactions = transformWaterToTransactions(
                                waterData.entries,
                                waterData.invoices
                            );

                            if (filterType === 'water') {
                                // Only water transactions
                                combinedTransactions = waterTransactions;
                                const waterAging = calculateWaterAging(waterData.invoices);
                                combinedAgingData = waterAging;
                            } else {
                                // Add water to existing transactions
                                combinedTransactions = [...combinedTransactions, ...waterTransactions];
                                const waterAging = calculateWaterAging(waterData.invoices);
                                combinedAgingData.aging15 += waterAging.aging15;
                                combinedAgingData.aging30 += waterAging.aging30;
                                combinedAgingData.aging60 += waterAging.aging60;
                                combinedAgingData.aging90 += waterAging.aging90;
                                combinedAgingData.totalOutstanding += waterAging.totalOutstanding;
                            }
                        }
                    }
                } catch (waterError) {
                    console.error("Error fetching water data:", waterError);
                    if (filterType === 'water') {
                        // If only water filter and no data, show empty
                        combinedTransactions = [];
                        combinedAgingData = {
                            aging15: 0,
                            aging30: 0,
                            aging60: 0,
                            aging90: 0,
                            totalOutstanding: 0
                        };
                    }
                }
            }

            setTransactions(combinedTransactions);
            setAgingData([combinedAgingData]);

        } catch (err) {
            toastify(err.message, "error");
            setTransactions([]);
            setAgingData([]);
        } finally {
            setLoading(false);
        }
    };

    const calculateTransactionAging = (transactions) => {
        const aging = {
            aging15: 0,
            aging30: 0,
            aging60: 0,
            aging90: 0,
            totalOutstanding: 0
        };

        if (!transactions || transactions.length === 0) {
            return aging;
        }

        const now = new Date();

        transactions.forEach(transaction => {
            const debit = parseFloat(transaction.debit) || 0;
            const credit = parseFloat(transaction.credit) || 0;

            // Outstanding amount for this transaction
            const outstanding = debit - credit;

            // Don't include transactions that are fully paid / credits
            if (outstanding <= 0) {
                return;
            }

            const transactionDate = new Date(transaction.date);

            if (isNaN(transactionDate.getTime())) {
                return;
            }

            const diffDays = Math.floor(
                (now - transactionDate) / (1000 * 60 * 60 * 24)
            );

            aging.totalOutstanding += outstanding;

            if (diffDays <= 15) {
                aging.aging15 += outstanding;
            } else if (diffDays <= 30) {
                aging.aging30 += outstanding;
            } else if (diffDays <= 60) {
                aging.aging60 += outstanding;
            } else {
                aging.aging90 += outstanding;
            }
        });

        return aging;
    };

    // Helper function to transform water data to match transaction structure
    const transformWaterToTransactions = (entries, invoices) => {
        if (!entries || !invoices) return [];

        return entries.map(entry => {
            const invoice = invoices.find(inv => inv._id === entry.invoiceId);
            return {
                date: entry.date || invoice?.date || new Date().toISOString(),
                transactionRef: invoice?.invoiceNumber || entry.reference || 'WATER-' + entry._id,
                details: entry.description || invoice?.description || 'Water Invoice',
                debit: entry.debit || 0,
                credit: entry.credit || 0,
                balance: entry.balance || 0,
                invoiceType: 'water',
                _id: entry.invoiceId || entry._id,
                // Keep original data for reference
                originalData: entry,
                invoiceData: invoice
            };
        });
    };

    // Helper function to calculate water aging
    const calculateWaterAging = (invoices) => {
        const aging = {
            aging15: 0,
            aging30: 0,
            aging60: 0,
            aging90: 0,
            totalOutstanding: 0
        };

        if (!invoices) return aging;

        const now = new Date();
        invoices.forEach(invoice => {
            if (invoice.status === 'paid' || invoice.status === 'PAID') return;

            const invoiceDate = new Date(invoice.date);
            const diffDays = Math.floor((now - invoiceDate) / (1000 * 60 * 60 * 24));
            const amount = invoice.amount || 0;

            aging.totalOutstanding += amount;

            if (diffDays <= 15) {
                aging.aging15 += amount;
            } else if (diffDays <= 30) {
                aging.aging30 += amount;
            } else if (diffDays <= 60) {
                aging.aging60 += amount;
            } else {
                aging.aging90 += amount;
            }
        });

        return aging;
    };

    // Handle filter change from dropdown
    const handleFilterChange = (e) => {
        const selectedFilter = e.value || e.target?.value || 'all';
        setFilterType(selectedFilter);
    };

    const fetchInvoiceTransactions = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getUnitInvoiceTransactionsURL}/${facilityId}/${unitId}`, "GET", {});

            if (response.success) {
                setInvoiceTransactions(response.data.invoices);
                setAllTransactions(response.data.invoices);
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, "error");
        }
    }

    const statusBodyTemplate = (row) => {
        let bgColor, textColor;
        switch (row.status) {
            case 'Paid':
                bgColor = '#e8f5e9';
                textColor = '#388e3c';
                break;
            case 'Partially Paid':
                bgColor = '#fff8e1';
                textColor = '#f9a825';
                break;
            default:
                bgColor = '#ffebee';
                textColor = '#d32f2f';
        }
        return (
            <span
                style={{
                    backgroundColor: bgColor,
                    color: textColor,
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    display: 'inline-block',
                    minWidth: '100px',
                    textAlign: 'center',
                }}
            >
                {row.status}
            </span>
        );
    };

    const fetchBillerAddresses = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getBillerAddressesURL}/${facilityId}`, 'GET');

            if (response && response.success && response.data) {
                // Handle both array and object responses
                let addresses = [];
                if (Array.isArray(response.data)) {
                    addresses = response.data;
                } else if (typeof response.data === 'object' && response.data !== null) {
                    // If it's an object, try to extract array from common properties
                    if (response.data.data && Array.isArray(response.data.data)) {
                        addresses = response.data.data;
                    } else if (response.data.addresses && Array.isArray(response.data.addresses)) {
                        addresses = response.data.addresses;
                    } else {
                        // If it's a single object, wrap it in an array
                        addresses = [response.data];
                    }
                }
                setBillerAddressList(addresses);
            } else if (response && response.data) {
                let addresses = [];
                if (Array.isArray(response.data)) {
                    addresses = response.data;
                } else if (typeof response.data === 'object' && response.data !== null) {
                    addresses = [response.data];
                }
                setBillerAddressList(addresses);
            } else {
                setBillerAddressList([]);
            }
        } catch (error) {
            toastify('Failed to fetch biller addresses', 'error');
            setBillerAddressList([]);
        }
    };

    const getDefaultBillerAddress = () => {
        if (!billerAddressList?.length) {
            return null;
        }

        return (
            billerAddressList.find(address => address.isDefault) ||
            billerAddressList[0]
        );
    };

    const exportPDF = async () => {
        const doc = new jsPDF();

        const currentDate = formatDate(new Date());

        const billerAddress = billerAddressList.find(
            address => address.isDefault
        ) || billerAddressList[0];

        const companyName = billerAddress?.companyName || "Company";
        const address = billerAddress?.address || "";
        const city = billerAddress?.city || "";
        const country = billerAddress?.country || "";
        const email = billerAddress?.email || "";

        // ---------- number helpers ----------
        const toNum = (value) => {
            if (value === null || value === undefined) return 0;
            if (typeof value === 'number') return isNaN(value) ? 0 : value;
            if (typeof value === 'string') {
                const n = parseFloat(value.replace(/[^0-9.-]+/g, ""));
                return isNaN(n) ? 0 : n;
            }
            return 0;
        };

        const formatCurrencyForPDF = (value) =>
            toNum(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

        // Signed format — keeps the "-" for negative balances
        const formatSignedCurrency = (value) => {
            const n = toNum(value);
            const abs = Math.abs(n).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return n < 0 ? `- ${abs}` : abs;
        };

        // Logo - failure must not prevent PDF export
        const logoBase64 = facilityLogo;

        if (logoBase64) {
            try {
                doc.addImage(logoBase64, 'PNG', 14, 10, 40, 20);
            } catch (error) {
                console.warn(
                    "Facility logo could not be added to PDF. Continuing without logo.",
                    error
                );
            }
        }

        // ---------- header ----------
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 78, 152);
        doc.text(companyName, 195, 15, { align: 'right' });

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);

        const location = [address, city, country].filter(Boolean).join(', ');
        if (location) doc.text(location, 195, 25, { align: 'right' });
        if (email) doc.text(`Email: ${email}`, 195, 30, { align: 'right' });

        const unitName = unit.name;

        // Document title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 78, 152);
        doc.text(`${unitName} - STATEMENT OF ACCOUNT`, 14, 45);

        // Document date
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(`Date: ${currentDate}`, 14, 55);

        // ---------------------------------------------------------
        // Account summary — MATCH FOOTER
        // ---------------------------------------------------------

        const totalDebit = transactions.reduce(
            (sum, t) => sum + toNum(t.debit), 0
        );

        const totalCredit = transactions.reduce(
            (sum, t) => sum + toNum(t.credit), 0
        );

        // Overpayment is already inside totalCredit — display-only subset
        const overpayment = transactions
            .filter(t => t.isOverpayment)
            .reduce((sum, t) => sum + toNum(t.credit), 0);

        // Use backend's authoritative currentBalance
        const finalBalance = toNum(summary?.currentBalance);

        const isOwing = finalBalance > 0;
        const displayBalance = isOwing
            ? `+ KES ${Math.abs(finalBalance).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
            : `- KES ${Math.abs(finalBalance).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;

        const balanceLabel = isOwing
            ? `Current Balance: ${displayBalance} OWING`
            : `Current Balance: ${displayBalance} CREDIT`;

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(balanceLabel, 195, 55, { align: 'right' });
        doc.setFont('helvetica', 'normal');

        // ---------------------------------------------------------
        // Transaction table — backend order (oldest first), same as UI
        // ---------------------------------------------------------

        const orderedTransactions = transactions;

        const headers = [[
            "Date",
            "Transaction Ref",
            "Details",
            "Debit (KES)",
            "Credit (KES)",
            "Balance (KES)"
        ]];

        const data = orderedTransactions.map(t => [
            formatDate(t.date),
            t.transactionRef,
            t.details,
            toNum(t.debit) ? formatCurrencyForPDF(t.debit) : "",
            toNum(t.credit) ? formatCurrencyForPDF(t.credit) : "",
            formatSignedCurrency(t.balance)
        ]);

        autoTable(doc, {
            startY: 65,
            head: headers,
            body: data,
            styles: { fontSize: 9 },
            headStyles: {
                fillColor: [0, 78, 152],
                fontSize: 9,
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: {
                0: { cellWidth: 25 },
                1: { cellWidth: 30 },
                2: { cellWidth: 50 },
                3: { cellWidth: 25, halign: 'right' },
                4: { cellWidth: 25, halign: 'right' },
                5: { cellWidth: 25, halign: 'right' }
            },
            alternateRowStyles: { fillColor: [240, 240, 240] },
            margin: { top: 65 },
            // Highlight overpayment rows in purple (matches UI badge + PDF tint)
            didParseCell: (data) => {
                if (data.section === 'body' && data.row.index >= 0) {
                    const row = orderedTransactions[data.row.index];
                    if (row?.isOverpayment) {
                        data.cell.styles.fillColor = [245, 235, 255];
                        data.cell.styles.textColor = [110, 40, 160];
                    }
                }
            }
        });

        // ---------------------------------------------------------
        // Summary — MATCH FOOTER
        // ---------------------------------------------------------

        // Payments applied = credits excluding overpayment
        const totalPaymentsApplied = totalCredit - overpayment;

        const summaryData = [
            [
                "Total Invoiced:",
                `KES ${formatCurrencyForPDF(totalDebit)}`
            ],
            [
                "Total Payments:",
                `KES ${formatCurrencyForPDF(totalPaymentsApplied)}`
            ]
        ];

        if (overpayment > 0) {
            summaryData.push([
                "Overpayment:",
                `KES ${formatCurrencyForPDF(overpayment)}`
            ]);
        }

        summaryData.push([
            isOwing ? "Current Balance (Owing):" : "Current Balance (Credit):",
            `KES ${displayBalance}`
        ]);

        const transactionTableY = doc.lastAutoTable.finalY;

        autoTable(doc, {
            startY: transactionTableY + 10,
            body: summaryData,
            theme: 'plain',
            styles: { fontSize: 10 },
            columnStyles: {
                0: { cellWidth: 100, fontStyle: 'bold', textColor: [0, 78, 152] },
                1: { cellWidth: 50, halign: 'right', fontStyle: 'bold' }
            },
            didParseCell: (data) => {
                if (data.section === 'body') {
                    const label = summaryData[data.row.index][0];
                    if (label === 'Overpayment:') {
                        data.cell.styles.textColor = [110, 40, 160];
                    }
                    if (data.row.index === summaryData.length - 1) {
                        data.cell.styles.textColor = isOwing
                            ? [185, 28, 28]
                            : [110, 40, 160];
                    }
                }
            }
        });

        // ---------------------------------------------------------
        // Aging table
        // ---------------------------------------------------------

        const summaryTableY = doc.lastAutoTable.finalY;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 78, 152);
        doc.text("Aging Summary", 14, summaryTableY + 20);

        const agingHeaders = [[
            "15 Days",
            "30 Days",
            "60 Days",
            "90+ Days",
            "Total Outstanding"
        ]];

        const agingRows = agingData.length > 0
            ? [[
                formatCurrencyForPDF(agingData[0].aging15) || "0.00",
                formatCurrencyForPDF(agingData[0].aging30) || "0.00",
                formatCurrencyForPDF(agingData[0].aging60) || "0.00",
                formatCurrencyForPDF(agingData[0].aging90) || "0.00",
                formatCurrencyForPDF(agingData[0].totalOutstanding) || "0.00"
            ]]
            : [["0.00", "0.00", "0.00", "0.00", "0.00"]];

        autoTable(doc, {
            startY: summaryTableY + 25,
            head: agingHeaders,
            body: agingRows,
            styles: { fontSize: 9, halign: 'right' },
            headStyles: {
                fillColor: [0, 78, 152],
                fontStyle: 'bold',
                halign: 'center'
            },
            theme: 'grid',
            alternateRowStyles: { fillColor: [240, 240, 240] }
        });

        // Optional: surface overpayment credit below aging, same as customer
        if (overpayment > 0) {
            const overpayY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(110, 40, 160);
            doc.text(
                `Overpayment credit available: KES ${formatCurrencyForPDF(overpayment)}`,
                14,
                overpayY
            );
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'normal');
        }

        doc.save(
            `statement_of_account_${currentDate.replace(/\//g, '-')}.pdf`
        );
    };

    const formatCurrency = (amount, currencyCode = 'KES') => {
        if (typeof amount !== 'number') return `${currencyCode} 0.00`;
        return `${currencyCode} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const HandleFilter = async (status) => {
        try {
            if (status === 'All') {
                setInvoiceTransactions(allTransactions);
            } else {
                const results = invoiceTransactions.filter((x) =>
                    x.status === status
                );
                setInvoiceTransactions(results);
            }
        } catch (error) {
            toastify(error.message, 'error');
        }
    };

    useEffect(() => {
        if (unitId) {
            fetchStatementOfAccounts(filterType);
        }
    }, [unitId, filterType]);

    useEffect(() => {
        getFacilityLogo();
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            await getUnit(); // this sets customerId
            getUnitOccupants();
            fetchBillerAddresses();
            getDefaultBillerAddress();
            fetchStatementOfAccounts();
            fetchInvoiceTransactions();
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (unitId) {  // ✅ only fetch when unitId exists
            getUnitDocuments();
            fetchDocumentTypes();
        }
    }, [unitId]);

    // Fetch water meter history when active tab changes to 'list'
    useEffect(() => {
        if (activeSubTab === 'list') {
            fetchWaterMeterHistory();
        } else if (activeSubTab === 'history') {
            fetchPowerMeterHistory();
        }
    }, [activeSubTab]);

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/core/dashboard/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Unit Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>View Unit</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header">
                                    <Link to={"#"} onClick={() => navigate(-1)}><span><i className="ti ti-arrow-narrow-left"></i> Back</span></Link>
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-body py-0">
                                    <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-1' ? 'active' : ''}`}
                                                id="profile-tab-1"
                                                onClick={() => handleTabClick('profile-1')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-1'}
                                            >
                                                <i className="ti ti-building-bank me-2"></i>Info
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-2' ? 'active' : ''}`}
                                                id="profile-tab-2"
                                                onClick={() => handleTabClick('profile-2')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-2'}
                                            >
                                                <i className="ti ti-file me-2"></i>Unit Documents
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-3' ? 'active' : ''}`}
                                                id="profile-tab-2"
                                                onClick={() => handleTabClick('profile-3')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-3'}
                                            >
                                                <i className="ti ti-building-warehouse me-2"></i>Unit History
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-4' ? 'active' : ''}`}
                                                id="profile-tab-4"
                                                onClick={() => handleTabClick('profile-4')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-4'}
                                            >
                                                <i className="ti ti-history me-2"></i>Utility History
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-5' ? 'active' : ''}`}
                                                id="profile-tab-7"
                                                onClick={() => handleTabClick('profile-5')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-5'}
                                            >
                                                <i className="ti ti-file-text me-2"></i>Invoice Payment Status
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-6' ? 'active' : ''}`}
                                                id="profile-tab-6"
                                                onClick={() => handleTabClick('profile-6')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-6'}
                                            >
                                                <i className="ti ti-report-money me-2"></i>Statement of Accounts
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-7' ? 'active' : ''}`}
                                                id="profile-tab-9"
                                                onClick={() => handleTabClick('profile-7')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-8'}
                                            >
                                                <i className="ti ti-clipboard-check me-2"></i>Inspection Checklist
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link cursor-pointer ${activeTab === 'profile-8' ? 'active' : ''}`}
                                                id="profile-tab-5"
                                                onClick={() => handleTabClick('profile-8')}
                                                role="tab"
                                                aria-selected={activeTab === 'profile-8'}
                                            >
                                                <i className="ti ti-settings me-2"></i>Settings
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="tab-content">
                                {/* For the info tab, keep layout as-is */}
                                {activeTab === 'profile-1' ? (
                                    <div className="tab-pane show active" id="profile-1" role="tabpanel" aria-labelledby="profile-tab-1">
                                        <div className="row">
                                            <div className="col-lg-4 col-xxl-3">
                                                <div className="card">
                                                    <div className="card-body position-relative">
                                                        <div className="text-center mt-3">
                                                            <div className="chat-avtar d-inline-flex mx-auto">
                                                                <i className="ti ti-building-bank me-2" style={{ fontSize: 100 }}></i>
                                                            </div>
                                                            <h5 className="mb-0"></h5>
                                                            <h2 className="text-muted mt-2">{unit.name}</h2>
                                                            <hr className="my-3 border border-secondary-subtle" />
                                                            <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">

                                                                <p className="mb-0">
                                                                    <strong>Residence Status: </strong>
                                                                    {(() => {
                                                                        const hasTenant = unit.tenant && unit.tenantId;
                                                                        const hasHomeOwner = unit.homeOwner && unit.homeOwnerId;
                                                                        const hasResident = unit.residentId;

                                                                        // Case 1: Tenant resides there (tenantId === residentId)
                                                                        // This case should NOT require homeOwner to exist
                                                                        if (hasTenant && hasResident && unit.tenantId === unit.residentId) {
                                                                            // Additional check: only if homeOwner is not the resident
                                                                            if (!hasHomeOwner || unit.homeOwnerId !== unit.residentId) {
                                                                                return (
                                                                                    <>
                                                                                        <Link to={`/facility/customer_management/view_customer/${unit.tenantId}`} className="text-primary fw-bold">
                                                                                            {unit.tenant.fullName}
                                                                                        </Link>{" "}
                                                                                        (Tenant) resides here
                                                                                    </>
                                                                                );
                                                                            }
                                                                        }

                                                                        // Case 2: Homeowner resides there (homeOwnerId === residentId)
                                                                        if (hasHomeOwner && hasResident && unit.homeOwnerId === unit.residentId) {
                                                                            return (
                                                                                <>
                                                                                    <Link to={`/facility/customer_management/view_customer/${unit.homeOwnerId}`} className="text-primary fw-bold">
                                                                                        {unit.homeOwner.fullName}
                                                                                    </Link>{" "}
                                                                                    (Owner) resides here
                                                                                </>
                                                                            );
                                                                        }

                                                                        // Case 3: Homeowner exists but is not residing
                                                                        if (hasHomeOwner && (!hasResident || unit.homeOwnerId !== unit.residentId)) {
                                                                            return (
                                                                                <>
                                                                                    Owned by{" "}
                                                                                    <Link to={`/facility/customer_management/view_customer/${unit.homeOwnerId}`} className="text-primary fw-bold">
                                                                                        {unit.homeOwner.fullName}
                                                                                    </Link>{" "}
                                                                                    but not residing
                                                                                </>
                                                                            );
                                                                        }

                                                                        // Case 4: No homeowner but has tenant (not residing)
                                                                        if (hasTenant && !hasHomeOwner) {
                                                                            return (
                                                                                <>
                                                                                    Leased to{" "}
                                                                                    <Link to={`/facility/customer_management/view_customer/${unit.tenantId}`} className="text-primary fw-bold">
                                                                                        {unit.tenant.fullName}
                                                                                    </Link>{" "}
                                                                                    (Tenant not residing)
                                                                                </>
                                                                            );
                                                                        }

                                                                        // Case 5: No homeOwnerId, no tenantId, no residentId – Vacant
                                                                        return 'No current resident';
                                                                    })()}
                                                                </p>

                                                            </div>

                                                            <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                <p className="mb-0"><strong>Unit Type: </strong>{unit.unitType}</p>
                                                            </div>
                                                            <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                <p className="mb-0"><strong>Block: </strong>{unit.division}</p>
                                                            </div>
                                                            <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                <p className="mb-0"><strong>Floor No: </strong>{unit.floorUnitNo} {defaultMeasurement.symbol}</p>
                                                            </div>
                                                            {Number(unit?.grossArea) > 1 && (
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Unit Size: </strong>{unit.grossArea}</p>
                                                                </div>
                                                            )}

                                                            {Number(unit?.netLettableArea) > 1 && (
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Net Lettable Area: </strong>{unit.netLettableArea} {defaultMeasurement.symbol}</p>
                                                                </div>
                                                            )}

                                                            {unit?.landRateNumber && unit.landRateNumber !== "0" && (
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Land Rate Number: </strong>{unit.landRateNumber}</p>
                                                                </div>
                                                            )}

                                                            {unit?.waterMeterNumber && unit.waterMeterNumber !== "0" && (
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Water Meter Number: </strong>{unit.waterMeterNumber}</p>
                                                                </div>
                                                            )}

                                                            {unit?.powerMeterNumber && unit.powerMeterNumber !== "0" && (
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Power Meter Number: </strong>{unit.powerMeterNumber}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-lg-8 col-xxl-9">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="dt-responsive table-responsive">
                                                            <h4 className="text-xl font-semibold mb-4">Unit Owner</h4>
                                                            <DataTable
                                                                value={homeOwner && homeOwner.customerType === "home owner" ? [homeOwner] : []}
                                                                emptyMessage="No homeowners found."
                                                                sortMode="multiple"
                                                                paginator
                                                                rows={10}
                                                                stripedRows
                                                                tableStyle={{ minWidth: '50rem' }}
                                                            >
                                                                <Column
                                                                    field="customerType"
                                                                    header="Occupant Type"
                                                                    body={(rowData) => (
                                                                        <span className="font-semibold">
                                                                            {rowData.customerType === "home owner"
                                                                                ? "Homeowner"
                                                                                : rowData.customerType === "tenant"
                                                                                    ? "Tenant"
                                                                                    : "Resident"}
                                                                        </span>
                                                                    )}
                                                                />
                                                                <Column field="fullName" header="Full Name" />
                                                                <Column field="email" header="Email" />
                                                                <Column field="phoneNumber" header="Phone" />
                                                            </DataTable>
                                                        </div>
                                                    </div>

                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={`tab-pane show active`} id={activeTab} role="tabpanel" aria-labelledby={`profile-tab-${activeTab.split('-')[1]}`}>
                                        <div className="row">
                                            {/* Show info panel conditionally */}
                                            {infoVisible && (
                                                <div className="col-lg-4 col-xxl-3">
                                                    <div className="card">
                                                        <div className="card-header d-flex justify-content-between align-items-center py-2">
                                                            <h5 className="m-0">Unit Information</h5>
                                                            <button
                                                                className="btn btn-sm btn-light"
                                                                onClick={toggleInfoPanel}
                                                                title="Hide Info Panel"
                                                            >
                                                                <i className="ti ti-chevron-left"></i>
                                                            </button>
                                                        </div>
                                                        <div className="card-body position-relative">
                                                            <div className="text-center mt-3">
                                                                <div className="chat-avtar d-inline-flex mx-auto">
                                                                    <i className="ti ti-building-bank me-2" style={{ fontSize: 100 }}></i>
                                                                </div>
                                                                <h5 className="mb-0"></h5>
                                                                <h2 className="text-muted mt-2">{unit.name}</h2>
                                                                <hr className="my-3 border border-secondary-subtle" />
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0">
                                                                        <strong>Residence Status: </strong>
                                                                        {(() => {
                                                                            const hasTenant = unit.tenant && unit.tenantId;
                                                                            const hasHomeOwner = unit.homeOwner && unit.homeOwnerId;
                                                                            const hasResident = unit.residentId;

                                                                            // Case 1: Tenant resides there (tenantId === residentId and homeOwnerId ≠ residentId)
                                                                            if (hasTenant && hasHomeOwner && hasResident && unit.tenantId === unit.residentId && unit.homeOwnerId !== unit.residentId) {
                                                                                return (
                                                                                    <>
                                                                                        <Link to={`/facility/customer_management/view_customer/${unit.tenantId}`} className="text-primary fw-bold">
                                                                                            {unit.tenant.fullName}
                                                                                        </Link>{" "}
                                                                                        (Tenant) resides here
                                                                                    </>
                                                                                );
                                                                            }

                                                                            // Case 2: Homeowner resides there (homeOwnerId === residentId and no tenant)
                                                                            if (hasHomeOwner && hasResident && !hasTenant && unit.homeOwnerId === unit.residentId) {
                                                                                return (
                                                                                    <>
                                                                                        <Link to={`/facility/customer_management/view_customer/${unit.homeOwnerId}`} className="text-primary fw-bold">
                                                                                            {unit.homeOwner.fullName}
                                                                                        </Link>{" "}
                                                                                        (Owner) resides here
                                                                                    </>
                                                                                );
                                                                            }

                                                                            // Case 3: Homeowner exists but is not residing (no residentId or residentId ≠ homeOwnerId, and no tenant)
                                                                            if (hasHomeOwner && (!hasResident || unit.homeOwnerId !== unit.residentId)) {
                                                                                return (
                                                                                    <>
                                                                                        Owned by{" "}
                                                                                        <Link to={`/facility/customer_management/view_customer/${unit.homeOwnerId}`} className="text-primary fw-bold">
                                                                                            {unit.homeOwner.fullName}
                                                                                        </Link>{" "}
                                                                                        but not residing
                                                                                    </>
                                                                                );
                                                                            }

                                                                            // Case 4: No homeOwnerId, no tenantId, no residentId – Vacant
                                                                            return 'No current resident';
                                                                        })()}
                                                                    </p>
                                                                </div>

                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Unit Type: </strong>{unit.unitType}</p>
                                                                </div>
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Block: </strong>{unit.division}</p>
                                                                </div>
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Floor No: </strong>0 {defaultMeasurement.symbol}</p>
                                                                </div>
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Unit Size: </strong>123456</p>
                                                                </div>
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Land Rate Number: </strong>1500 {defaultMeasurement.symbol}</p>
                                                                </div>
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Water Meter Number: </strong>1500</p>
                                                                </div>
                                                                <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                    <p className="mb-0"><strong>Power Meter Number: </strong>1500</p>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Property Management Information */}
                                            {isManagedByPropertyManager && (
                                                <>
                                                    <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                        <p className="mb-0">
                                                            <strong>Property Management: </strong>
                                                            <span className="badge bg-success">Managed Unit</span>
                                                        </p>
                                                    </div>
                                                    <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                        <p className="mb-0">
                                                            <strong>Property Manager: </strong>
                                                            {unit.propertyManagerName || 'Not specified'}
                                                        </p>
                                                    </div>
                                                </>
                                            )}

                                            {/* Content area that expands when info is hidden */}
                                            <div className={`col-lg-${infoVisible ? '8' : '12'} col-xxl-${infoVisible ? '9' : '12'}`}>
                                                {/* Show Unit Info Button when Info Panel is Hidden */}
                                                {!infoVisible && (
                                                    <button
                                                        className="btn btn-primary mb-3"
                                                        onClick={toggleInfoPanel}
                                                        title="Show Unit Info"
                                                    >
                                                        <i className="ti ti-building-bank me-2"></i>Show Unit Info
                                                    </button>
                                                )}

                                                {activeTab === 'profile-2' && (
                                                    <div className="container mt-4">
                                                        <div className="row">
                                                            <div className="col-12 d-flex justify-content-between">
                                                                <h5>Unit Documents</h5>
                                                                <Button label="Add Document" className="btn btn-primary" icon="pi pi-plus" onClick={() => setDocumentModal(true)} />
                                                            </div>
                                                        </div>
                                                        <div className="row mt-3">
                                                            <div className="col-12">
                                                                <DataTable value={unitDocuments || []} paginator rows={5} emptyMessage="No documents found.">
                                                                    <Column field="documentName" header="Document Name"></Column>
                                                                    <Column
                                                                        header="Document Type"
                                                                        body={(rowData) => rowData.documentType?.name || "N/A"}
                                                                    ></Column>

                                                                    <Column header="Document" body={(rowData) => renderDocument(rowData)}></Column>
                                                                </DataTable>
                                                            </div>
                                                        </div>
                                                        {/* Modal for Adding Document */}
                                                        <Dialog header="Add Document" visible={documentModal} style={{ width: "400px" }} onHide={() => setDocumentModal(false)}>
                                                            <div className="p-fluid">
                                                                <div className="field mb-3">
                                                                    <label className='form-label'>Document Name</label>
                                                                    <InputText value={documentName} onChange={(e) => setDocumentName(e.target.value)} />
                                                                </div>
                                                                <div className="field mb-3">
                                                                    <label className='form-label'>Document Type</label>
                                                                    <select className="form-control" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
                                                                        <option value="">Select Document Type</option>

                                                                        {documentTypes?.length > 0 ? (
                                                                            documentTypes.map((type) => (
                                                                                <option key={type._id} value={type._id}>
                                                                                    {type.name}
                                                                                </option>
                                                                            ))
                                                                        ) : (
                                                                            <option value="" disabled>No document types available</option>
                                                                        )}
                                                                    </select>

                                                                </div>

                                                                <div className="field mb-3">
                                                                    <label className='form-label'>Upload Document</label>
                                                                    <input
                                                                        type="file"
                                                                        className="form-control"
                                                                        onChange={(e) => setFile(e.target.files[0])}
                                                                    />
                                                                </div>
                                                                <div className="d-flex justify-content-end mt-3">
                                                                    <Button label="Cancel" className="p-button-text" onClick={() => setDocumentModal(false)} />
                                                                    <Button label="Save" className="p-button-primary ms-2" onClick={handleDocumentSubmit} />
                                                                </div>
                                                            </div>
                                                        </Dialog>

                                                    </div>
                                                )}

                                                {activeTab === 'profile-3' && (
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="dt-responsive table-responsive mb-6">
                                                                <h4 className="text-xl font-semibold mb-4">Current Occupants</h4>
                                                                <DataTable
                                                                    value={activeOccupants}
                                                                    emptyMessage="No current occupants found."
                                                                    sortMode="multiple"
                                                                    paginator
                                                                    rows={10}
                                                                    stripedRows
                                                                    tableStyle={{ minWidth: '50rem' }}
                                                                >
                                                                    <Column field="customerType" header="Occupant Type" body={(rowData) => (
                                                                        <span className="font-semibold">
                                                                            {rowData.customerType === "home owner" ? "Homeowner" : rowData.customerType === "tenant" ? "Tenant" : "Resident"}
                                                                        </span>
                                                                    )} />
                                                                    <Column field="customerDetails.fullName" header="Full Name" />
                                                                    <Column field="customerDetails.email" header="Email" />
                                                                    <Column field="customerDetails.phoneNumber" header="Phone" />
                                                                    <Column field="moveInDate" header="Move In Date" body={(rowData) => (
                                                                        <span>{new Date(rowData.moveInDate).toLocaleDateString()}</span>
                                                                    )} />
                                                                </DataTable>
                                                            </div>

                                                            <div className="dt-responsive table-responsive">
                                                                <h4 className="text-xl font-semibold mb-4">Previous Occupants</h4>
                                                                <DataTable
                                                                    value={pastOccupants}
                                                                    emptyMessage="No previous occupants found."
                                                                    sortMode="multiple"
                                                                    paginator
                                                                    rows={10}
                                                                    stripedRows
                                                                    tableStyle={{ minWidth: '50rem' }}
                                                                    sortField="moveOutDate"
                                                                    sortOrder={-1}
                                                                >
                                                                    <Column field="customerType" header="Occupant Type" body={(rowData) => (
                                                                        <span className="font-semibold">
                                                                            {rowData.customerType === "home owner" ? "Homeowner" : rowData.customerType === "tenant" ? "Tenant" : "Resident"}
                                                                        </span>
                                                                    )} />
                                                                    <Column field="customerDetails.fullName" header="Full Name" />
                                                                    <Column field="customerDetails.email" header="Email" />
                                                                    <Column field="customerDetails.phoneNumber" header="Phone" />
                                                                    <Column field="moveInDate" header="Move In Date" body={(rowData) => (
                                                                        <span>{new Date(rowData.moveInDate).toLocaleDateString()}</span>
                                                                    )} />
                                                                    <Column field="moveOutDate" header="Move Out Date" body={(rowData) => (
                                                                        <span>{new Date(rowData.moveOutDate).toLocaleDateString()}</span>
                                                                    )} />
                                                                    <Column
                                                                        header="Statement"
                                                                        body={(rowData) => (
                                                                            <Button
                                                                                label="View SOA"
                                                                                className="p-button-text p-button-sm"
                                                                                onClick={() => navigate(`/facility/customer_management/view_customer/${rowData.customerId}`)}
                                                                            />
                                                                        )}
                                                                    />
                                                                    <Column field="customerDetails.status" header="Customer Status" body={(rowData) => (
                                                                        <span className={`font-semibold ${rowData.customerDetails?.status === 'Active' ? 'text-green-600' : 'text-gray-500'}`}>
                                                                            {rowData.customerDetails?.status || 'Unknown'}
                                                                        </span>
                                                                    )} />
                                                                </DataTable>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {activeTab === 'profile-5' && (
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="row">

                                                                <div className="col-12">
                                                                    <div className="mb-4 p-3 border rounded-1 shadow-sm bg-white">
                                                                        <div className="card-header d-flex justify-content-between align-items-start">
                                                                            <h5 className="m-0">Payment Status</h5>
                                                                            <div className="col-md-2 col-sm-4">
                                                                                <div className="btn-group-dropdown">
                                                                                    <button
                                                                                        className="btn btn-outline-default dropdown-toggle"
                                                                                        type="button"
                                                                                        data-bs-toggle="dropdown"
                                                                                        aria-haspopup="true"
                                                                                        aria-expanded="false"
                                                                                    >
                                                                                        <span>Filter By </span>
                                                                                        <i className="ti ti-filter"></i>
                                                                                    </button>
                                                                                    <div className="dropdown-menu">
                                                                                        <Link className="dropdown-item" to="#!" onClick={() => HandleFilter('All')}>
                                                                                            All
                                                                                        </Link>
                                                                                        <Link className="dropdown-item" to="#!" onClick={() => HandleFilter('Paid')}>
                                                                                            Paid
                                                                                        </Link>
                                                                                        <Link className="dropdown-item" to="#!" onClick={() => HandleFilter('Partially Paid')}>
                                                                                            Partially Paid
                                                                                        </Link>
                                                                                        <Link className="dropdown-item" to="#!" onClick={() => HandleFilter('Unpaid')}>
                                                                                            Unpaid
                                                                                        </Link>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div className='table-responsive'>
                                                                            <DataTable value={invoiceTransactions} paginator rows={5}
                                                                                stripedRows
                                                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                                                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Invoices"
                                                                                rowsPerPageOptions={[10, 15, 25, 50, 75, 100]}
                                                                            >
                                                                                <Column field="invoiceNumber" header="Invoice No"
                                                                                    body={(rowData) => {
                                                                                        const invoiceId = rowData._id;
                                                                                        const type = rowData.whatFor?.toLowerCase();
                                                                                        let link = '';
                                                                                        switch (type) {
                                                                                            case 'contract':
                                                                                                link = `/facility/levy_management/invoice/${invoiceId}`;
                                                                                                break;
                                                                                            case 'lease':
                                                                                                link = `/facility/lease_management/invoice/${invoiceId}`;
                                                                                                break;
                                                                                            default:
                                                                                                link = `/facility/levy_management/invoice/${invoiceId}`;
                                                                                                break;
                                                                                        }
                                                                                        return (
                                                                                            <Link
                                                                                                to={link}
                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                                className="text-blue-600"
                                                                                            >
                                                                                                {rowData.invoiceNumber}
                                                                                            </Link>
                                                                                        );
                                                                                    }} />

                                                                                <Column
                                                                                    field="invoiceType"
                                                                                    header="Details"
                                                                                    body={(row) => (
                                                                                        <span
                                                                                            style={{
                                                                                                background: '#e0f7fa',
                                                                                                color: '#00796b',
                                                                                                padding: '4px 8px',
                                                                                                borderRadius: '12px',
                                                                                                fontWeight: '500',
                                                                                                fontSize: '0.9rem'
                                                                                            }}
                                                                                        >
                                                                                            {row.invoiceType}
                                                                                        </span>
                                                                                    )}
                                                                                />
                                                                                <Column field="totalAmount" header="Amount"
                                                                                    body={(row) => (
                                                                                        <span style={{ fontWeight: '600', color: '#1e88e5' }}>
                                                                                            KES {(row.totalAmount).toLocaleString()}
                                                                                        </span>
                                                                                    )}
                                                                                />
                                                                                <Column
                                                                                    field="amountPaid"
                                                                                    header="Paid"
                                                                                    body={(row) => (
                                                                                        <span style={{ color: '#2e7d32', fontWeight: '600' }}>
                                                                                            KES {(row.amountPaid).toLocaleString()}
                                                                                        </span>
                                                                                    )}
                                                                                />
                                                                                <Column
                                                                                    header="Balance"
                                                                                    body={(row) => {
                                                                                        const balance = (row.totalAmount || 0) - (row.amountPaid || 0);
                                                                                        const color = balance > 0 ? '#d32f2f' : '#388e3c';
                                                                                        return (
                                                                                            <span style={{ color, fontWeight: '600' }}>
                                                                                                KES {balance.toLocaleString()}
                                                                                            </span>
                                                                                        );
                                                                                    }}
                                                                                />
                                                                                <Column field="status" header="Status" body={statusBodyTemplate} />
                                                                                <Column
                                                                                    field="createdAt"
                                                                                    header="Date"
                                                                                    sortable
                                                                                    body={(row) => new Date(row.createdAt).toLocaleDateString()}
                                                                                />
                                                                            </DataTable>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {activeTab === 'profile-6' && (
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <div className="row">
                                                                <div className="col-12">

                                                                    <div className="mb-4 p-3 border rounded-1 shadow-sm bg-white">
                                                                        <div className="card-header d-flex justify-content-between align-items-start">
                                                                            <h5 className="m-0">Transactions</h5>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                {/* Dropdown Filter */}
                                                                                <Dropdown
                                                                                    value={filterType}
                                                                                    options={filterOptions}
                                                                                    onChange={handleFilterChange}
                                                                                    placeholder="Filter Transactions"
                                                                                    className="p-2 border rounded"
                                                                                    style={{ minWidth: '180px' }}
                                                                                    optionLabel="label"
                                                                                    optionValue="value"
                                                                                />

                                                                                {/* Export Buttons */}
                                                                                <div className="d-flex gap-1">
                                                                                    {/* <Button type="button" label="CSV" rounded onClick={exportCSV} data-pr-tooltip="CSV" /> */}
                                                                                    {/* <Button type="button" label="XLS" severity="success" rounded onClick={exportXLSX} data-pr-tooltip="XLS" /> */}
                                                                                    <Button type="button" label="PDF" severity="warning" rounded onClick={exportPDF} data-pr-tooltip="PDF" />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className='table-responsive'>
                                                                            <DataTable
                                                                                value={transactions}
                                                                                emptyMessage={loading ? "Loading transactions..." : `No ${filterType === 'all' ? '' : filterType} transactions found.`}
                                                                                sortMode="multiple"
                                                                                sortField="date"
                                                                                sortOrder={1}
                                                                                removableSort={true}
                                                                                paginator
                                                                                rows={10}
                                                                                stripedRows
                                                                                className="shadow-md rounded-lg overflow-hidden"
                                                                                rowClassName={(data) =>
                                                                                    data.isOverpayment
                                                                                        ? "bg-purple-50 hover:bg-purple-100 transition-colors"
                                                                                        : "hover:bg-gray-50 transition-colors"
                                                                                }
                                                                                footer={
                                                                                    <div className="p-3 bg-gray-50 border-t">
                                                                                        <div className="text-sm text-gray-500 mb-2">
                                                                                            Showing {transactions?.length || 0} transactions
                                                                                        </div>
                                                                                        <div className="row fw-bold">
                                                                                            <div className="col">
                                                                                                <span className="text-muted">Totals:</span>
                                                                                            </div>
                                                                                            <div className="col text-end">
                                                                                                <span className="text-red-600">
                                                                                                    Debit: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(
                                                                                                        transactions?.reduce((sum, t) => sum + (parseFloat(t.debit) || 0), 0) || 0
                                                                                                    )}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="col text-end">
                                                                                                <span className="text-green-600">
                                                                                                    Credit: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(
                                                                                                        transactions?.reduce((sum, t) => sum + (parseFloat(t.credit) || 0), 0) || 0
                                                                                                    )}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="col text-end">
                                                                                                <span className="text-purple-600">
                                                                                                    Overpaid: {
                                                                                                        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(
                                                                                                            transactions
                                                                                                                ?.filter(t => t.isOverpayment)
                                                                                                                .reduce((sum, t) => sum + (parseFloat(t.credit) || 0), 0) || 0
                                                                                                        )
                                                                                                    }
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="col text-end">
                                                                                                {(() => {
                                                                                                    const totalCredit = transactions?.reduce((sum, t) => sum + (parseFloat(t.credit) || 0), 0) || 0;
                                                                                                    const totalDebit = transactions?.reduce((sum, t) => sum + (parseFloat(t.debit) || 0), 0) || 0;

                                                                                                    const netBalance = totalDebit - totalCredit; // positive = owing, negative = overpaid
                                                                                                    const isOverpaid = netBalance < 0;

                                                                                                    const displayValue = isOverpaid
                                                                                                        ? `+ ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' })
                                                                                                            .format(Math.abs(netBalance))} CREDIT`
                                                                                                        : `${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' })
                                                                                                            .format(netBalance)} OWING`;

                                                                                                    return (
                                                                                                        <span className={isOverpaid ? "text-blue-600 font-bold" : "text-red-600"}>
                                                                                                            Net Balance: {displayValue}
                                                                                                        </span>
                                                                                                    );
                                                                                                })()}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                }
                                                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                                                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} transactions"
                                                                                rowsPerPageOptions={[10, 15, 25, 50, 75, 100]}
                                                                            >
                                                                                <Column
                                                                                    field="date"
                                                                                    header="Date"
                                                                                    body={(rowData) => formatDate(rowData.date)}
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    sortable
                                                                                />

                                                                                {/* Transaction Type Column - NEW */}
                                                                                <Column
                                                                                    field="invoiceType"
                                                                                    header="Type"
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    body={(rowData) => {
                                                                                        const type = rowData.invoiceType?.toLowerCase() || 'unknown';
                                                                                        const colorMap = {
                                                                                            'contract': 'text-blue-600',
                                                                                            'lease': 'text-purple-600',
                                                                                            'water': 'text-green-600',
                                                                                            'unknown': 'text-gray-600'
                                                                                        };
                                                                                        const labelMap = {
                                                                                            'contract': 'Levy',
                                                                                            'lease': 'Lease',
                                                                                            'water': 'Water',
                                                                                            'unknown': 'Unknown'
                                                                                        };
                                                                                        return (
                                                                                            <span className={`font-medium ${colorMap[type] || 'text-gray-600'}`}>
                                                                                                {labelMap[type] || type}
                                                                                            </span>
                                                                                        );
                                                                                    }}
                                                                                    sortable
                                                                                />

                                                                                <Column
                                                                                    field="transactionRef"
                                                                                    header="Transaction Ref"
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    body={(rowData) => {
                                                                                        const invoiceId = rowData._id;
                                                                                        const type = rowData.invoiceType?.toLowerCase();

                                                                                        if (!invoiceId || !rowData.transactionRef) {
                                                                                            return <span className="text-gray-400 italic">No Ref</span>;
                                                                                        }

                                                                                        let link = '';
                                                                                        switch (type) {
                                                                                            case 'contract':
                                                                                                link = `/facility/levy_management/invoice/${invoiceId}`;
                                                                                                break;
                                                                                            case 'lease':
                                                                                                link = `/facility/lease_management/invoice/${invoiceId}`;
                                                                                                break;
                                                                                            case 'water':
                                                                                                // For water, maybe link to water invoice or just show as text
                                                                                                return <span className="text-green-600">{rowData.transactionRef}</span>;
                                                                                            default:
                                                                                                link = `/facility/levy_management/invoice/${invoiceId}`;
                                                                                                break;
                                                                                        }

                                                                                        return (
                                                                                            <Link
                                                                                                to={link}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                }}
                                                                                                className="text-blue-600 underline hover:text-blue-800"
                                                                                            >
                                                                                                {rowData.transactionRef}
                                                                                            </Link>
                                                                                        );
                                                                                    }}
                                                                                />

                                                                                <Column
                                                                                    field="details"
                                                                                    header="Details"
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    sortable
                                                                                    body={(rowData) => (
                                                                                        <span className="flex items-center gap-2">
                                                                                            {rowData.details}
                                                                                            {rowData.isOverpayment && (
                                                                                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                                                                                                    Overpayment
                                                                                                </span>
                                                                                            )}
                                                                                        </span>
                                                                                    )}
                                                                                />
                                                                                <Column
                                                                                    field="debit"
                                                                                    header="Debit"
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    body={(rowData) => rowData.debit && (
                                                                                        <span className="text-red-600 font-medium">
                                                                                            {typeof rowData.debit === 'number' ?
                                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.debit) :
                                                                                                rowData.debit}
                                                                                        </span>
                                                                                    )}
                                                                                    sortable
                                                                                />
                                                                                {/* <Column
                                                                                    field="credit"
                                                                                    header="Credit"
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    body={(rowData) => rowData.credit && (
                                                                                        <span className="text-green-600 font-medium">
                                                                                            {typeof rowData.credit === 'number' ?
                                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.credit) :
                                                                                                rowData.credit}
                                                                                        </span>
                                                                                    )}
                                                                                    sortable
                                                                                /> */}
                                                                                <Column
                                                                                    field="credit"
                                                                                    header="Credit"
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    body={(rowData) => rowData.credit && (
                                                                                        <span className={
                                                                                            rowData.isOverpayment
                                                                                                ? "text-purple-600 font-semibold"
                                                                                                : "text-green-600 font-medium"
                                                                                        }>
                                                                                            {typeof rowData.credit === 'number' ?
                                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.credit) :
                                                                                                rowData.credit}
                                                                                        </span>
                                                                                    )}
                                                                                    sortable
                                                                                />
                                                                                <Column
                                                                                    field="balance"
                                                                                    header="Balance"
                                                                                    headerClassName="bg-blue-100 font-bold"
                                                                                    body={(rowData) => (
                                                                                        <span className={`font-medium ${parseFloat(rowData.balance) < 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                                                                            {typeof rowData.balance === 'number' ?
                                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.balance) :
                                                                                                rowData.balance}
                                                                                        </span>
                                                                                    )}
                                                                                    sortable
                                                                                />
                                                                            </DataTable>
                                                                        </div>
                                                                    </div>
                                                                </div>



                                                                <div className="col-12">
                                                                    <div className="mb-4 p-3 border rounded-1 shadow-sm bg-white">
                                                                        <h4 className="mb-3">Overdue Aging Summary</h4>
                                                                        <div className='table-responsive'>
                                                                            <DataTable
                                                                                value={agingData}
                                                                                emptyMessage="No overdue invoices."
                                                                                tableStyle={{ minWidth: '40rem' }}
                                                                            >
                                                                                <Column field="aging15" header="15 Days" body={(rowData) => {
                                                                                    const amount = rowData.aging15;
                                                                                    return (
                                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                                            {formatCurrency(amount)}
                                                                                        </span>
                                                                                    );
                                                                                }}
                                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                                <Column field="aging30" header="30 Days" body={(rowData) => {
                                                                                    const amount = rowData.aging30;
                                                                                    return (
                                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                                            {formatCurrency(amount)}
                                                                                        </span>
                                                                                    );
                                                                                }}
                                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                                <Column field="aging60" header="60 Days" body={(rowData) => {
                                                                                    const amount = rowData.aging60;
                                                                                    return (
                                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                                            {formatCurrency(amount)}
                                                                                        </span>
                                                                                    );
                                                                                }}
                                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                                <Column field="aging90" header="90+ Days" body={(rowData) => {
                                                                                    const amount = rowData.aging90;
                                                                                    return (
                                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                                            {formatCurrency(amount)}
                                                                                        </span>
                                                                                    );
                                                                                }}
                                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                                <Column field="totalOutstanding" header="Total Outstanding" body={(rowData) => {
                                                                                    const amount = rowData.totalOutstanding;
                                                                                    return (
                                                                                        <span className={amount > 0 ? 'text-red-500 font-bold' : ''}>
                                                                                            {formatCurrency(amount)}
                                                                                        </span>
                                                                                    );
                                                                                }}
                                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                            </DataTable>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {activeTab === 'profile-4' && (
                                                    <div>
                                                        {/* Sub-Tabs for Analog Meters  */}
                                                        <ul className="nav nav-tabs">
                                                            <li className="nav-item">
                                                                <button
                                                                    className={`nav-link ${activeSubTab === 'list' ? 'active' : ''}`}
                                                                    onClick={() => setActiveSubTab('list')}
                                                                >
                                                                    Water Meter History
                                                                </button>
                                                            </li>
                                                            <li className="nav-item">
                                                                <button
                                                                    className={`nav-link ${activeSubTab === 'history' ? 'active' : ''}`}
                                                                    onClick={() => setActiveSubTab('history')}
                                                                >
                                                                    Power Meter History
                                                                </button>
                                                            </li>
                                                        </ul>
                                                        <div className="tab-content mt-3">
                                                            {/* Water Meter History content */}
                                                            {activeSubTab === 'list' && (
                                                                <div>
                                                                    {loading ? (
                                                                        <div className="text-center p-4">
                                                                            <i className="ti ti-loader animate-spin text-primary" style={{ fontSize: '2rem' }}></i>
                                                                            <p className="mt-2">Loading water meter data...</p>
                                                                        </div>
                                                                    ) : meterData ? (
                                                                        <div>
                                                                            <DataTable
                                                                                value={[meterData]} // Use an array with the meter object
                                                                                emptyMessage="No water meters found."
                                                                                stripedRows
                                                                                tableStyle={{ minWidth: '50rem' }}
                                                                            >
                                                                                <Column
                                                                                    field="meterNumber"
                                                                                    header="Meter S/N"
                                                                                    body={waterMeterNumberTemplate}
                                                                                    sortable
                                                                                />
                                                                                <Column field="status" header="Status" body={statusTemplate} sortable />
                                                                                <Column
                                                                                    field="previousReading"
                                                                                    header="Previous Reading"
                                                                                    sortable
                                                                                    body={(rowData) => formatReading(rowData.previousReading)}
                                                                                />
                                                                                <Column
                                                                                    field="currentReading"
                                                                                    header="Current Reading"
                                                                                    sortable
                                                                                    body={(rowData) => formatReading(rowData.currentReading)}
                                                                                />
                                                                                <Column
                                                                                    field="lastReadingDate"
                                                                                    header="Last Reading Date"
                                                                                    sortable
                                                                                    body={(rowData) => formatDate(rowData.lastReadingDate)}
                                                                                />
                                                                                <Column
                                                                                    field="lastUpdated"
                                                                                    header="Last Updated"
                                                                                    sortable
                                                                                    body={(rowData) => formatDate(rowData.lastUpdated)}
                                                                                />
                                                                            </DataTable>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info">
                                                                            <i className="ti ti-info-circle me-2"></i>
                                                                            No water meter found for this unit.
                                                                        </div>
                                                                    )}

                                                                </div>
                                                            )}
                                                            {activeSubTab === 'history' && (
                                                                <div>
                                                                    {loading ? (
                                                                        <div className="text-center p-4">
                                                                            <i className="ti ti-loader animate-spin text-primary" style={{ fontSize: '2rem' }}></i>
                                                                            <p className="mt-2">Loading power meter data...</p>
                                                                        </div>
                                                                    ) : powerMeterData ? (
                                                                        <div>
                                                                            <DataTable
                                                                                value={[powerMeterData]} // Wrap in array since it's a single object
                                                                                emptyMessage="No power meters found."
                                                                                stripedRows
                                                                                tableStyle={{ minWidth: '50rem' }}
                                                                            >
                                                                                <Column
                                                                                    field="meterSerialNumber"
                                                                                    header="Meter S/N"
                                                                                    body={powerMeterSerialTemplate}
                                                                                    sortable
                                                                                />
                                                                                <Column field="type" header="Meter Type" sortable />
                                                                                <Column
                                                                                    field="meterReading"
                                                                                    header="Current Reading (kWh)"
                                                                                    sortable
                                                                                    body={(rowData) => rowData.meterReading ? `${rowData.meterReading} kWh` : '0 kWh'}
                                                                                />
                                                                                <Column
                                                                                    field="currentMeterReading"
                                                                                    header="Latest Reading (kWh)"
                                                                                    sortable
                                                                                    body={(rowData) => rowData.currentMeterReading ? `${rowData.currentMeterReading} kWh` : '0 kWh'}
                                                                                />
                                                                                <Column field="liveStatus" header="Status" body={powerStatusTemplate} sortable />
                                                                                <Column
                                                                                    field="lastUpdated"
                                                                                    header="Last Updated"
                                                                                    sortable
                                                                                    body={(rowData) => formatDate(rowData.lastUpdated)}
                                                                                />
                                                                            </DataTable>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info">
                                                                            <i className="ti ti-info-circle me-2"></i>
                                                                            No power meter found for this unit.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {activeTab === 'profile-7' && (
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <HandoverInspectionTab facilityId={unit.facilityId} unitId={unitId} />
                                                        </div>
                                                    </div>
                                                )}

                                                {activeTab === 'profile-8' && (
                                                    <div className="card">
                                                        <div className="card-body py-0">
                                                            <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                                                                <li className="nav-item">
                                                                    <a className="nav-link active" id="setting-tab-1" data-bs-toggle="tab" href="#setting-1" role="tab" aria-selected="true">
                                                                        <i className="ti ti-edit me-2"></i>Edits
                                                                    </a>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        <div className="tab-content">
                                                            <div className="tab-pane active" id="setting-1" role="tabpanel" aria-labelledby="setting-tab-1">
                                                                <div className="row">
                                                                    <div className="col-lg-4">
                                                                    </div>
                                                                    <div className="col-lg-4">
                                                                        <div className="card">
                                                                            <div className="card-header">
                                                                                <b>Update Unit</b>
                                                                            </div>
                                                                            <div className="card-body" style={{ padding: '10px' }}>
                                                                                <label>Unit Name<span style={{ color: 'red' }}>*</span>:</label>
                                                                                <input className="form-control mt-2" type="text" placeholder="Enter unit name" value={unitName} onChange={(e) => {
                                                                                    let value = e.target.value;
                                                                                    setUnitName(value)
                                                                                }}></input>
                                                                            </div>
                                                                            <div className='card-body' style={{ padding: '10px' }}>
                                                                                <label>Unit Type<span style={{ color: 'red' }}>*</span>:</label>
                                                                                <select className="form-control" type="text" value={unitType} onChange={(e) => {
                                                                                    let value = e.target.value;
                                                                                    setUnitType(value)
                                                                                }}>
                                                                                    <option>-- Select --</option>
                                                                                    {
                                                                                        unitTypes !== undefined &&
                                                                                        unitTypes.map((item) => {
                                                                                            return <option value={item}>{item}</option>
                                                                                        })
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                            {/* Property Management Information - Only show if managed */}
                                                                            {isManagedByPropertyManager && (
                                                                                <div className="card-body" style={{ padding: '10px' }}>
                                                                                    <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                                                                                        <p className="mb-0">
                                                                                            <strong>Property Management: </strong>
                                                                                            <span className="badge bg-success">Managed by {unit.propertyManagerName || 'Property Manager'}</span>
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            <div className='mt-4 mb-2' style={{ marginRight: '5px' }}>
                                                                                <button className="btn btn-primary" style={{ float: 'right' }} onClick={() => { HandleUnitUpdate() }}>Save Changes</button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-lg-4">
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ViewUnit;
